import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Copy, Check, CaretDown, CaretUp } from "@phosphor-icons/react";
import BrandSpinner from "@/components/BrandSpinner";

/**
 * Caption AI · 4 didascalie pronte da copia-incolla.
 *
 * Toni: Editoriale · Amichevole · Urgente · Professionale (iter v25).
 * Pannello collassabile (iter v25): aperto di default, l'agente lo chiude
 * sul mobile per scorrere comodamente fino a Layout/Colore.
 *
 * iter v28 r14 — `occasion` propagata (open_house / venduto):
 *   - quando l'agente seleziona Open House / Venduto, il testo generato
 *     è coerente con l'occasione (invito porte aperte / celebrazione rogito).
 *   - i risultati cached vengono invalidati al cambio di occasion.
 *
 * Endpoint: GET /api/properties/{pid}/social/captions
 */
export default function CaptionGenerator({
  propertyId,
  occasion = "nuovo_annuncio",
  eventDate,
  eventTime,
  eventTimeEnd,
  daysToSell,
  // iter v28 r38 — Tono = single source of truth. Controlla anche cosa
  // finisce nel testo del Pacchetto Social (PackBox).
  tone,
  setTone,
}) {
  const [loading, setLoading] = useState(false);
  const [captions, setCaptions] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  // Invalidazione cache: se cambia occasione/data/ora/giorni/tono, i testi
  // precedenti non sono più validi → li resettiamo così l'agente vede
  // chiaramente che deve rigenerare.
  useEffect(() => {
    setCaptions(null);
  }, [occasion, eventDate, eventTime, eventTimeEnd, daysToSell, tone]);

  const onGenerate = async () => {
    setLoading(true);
    try {
      const params = {};
      if (occasion && occasion !== "nuovo_annuncio") params.occasion = occasion;
      if (occasion === "open_house") {
        if (eventDate) params.event_date = eventDate;
        if (eventTime) params.event_time = eventTime;
        if (eventTimeEnd) params.event_time_end = eventTimeEnd;
      } else if (occasion === "venduto") {
        if (daysToSell && Number(daysToSell) > 0) params.days_to_sell = Number(daysToSell);
      }
      if (tone) params.tone = tone;
      const { data } = await api.get(`/properties/${propertyId}/social/captions`, { params });
      setCaptions(data.captions || []);
    } catch (err) {
      console.error("captions error", err);
      toast.error("Non sono riuscito a scrivere i testi. Riprova fra un attimo.");
    } finally {
      setLoading(false);
    }
  };

  const onCopy = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      toast.success("Testo copiato.");
      setTimeout(() => setCopiedIdx(null), 1800);
    } catch {
      toast.error("Copia non riuscita. Selezionala manualmente.");
    }
  };

  return (
    <div
      className="mt-4 bg-white border border-[#EAE7DF] rounded-xl p-4"
      data-testid="caption-generator"
    >
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setCollapsed(v => !v)}
          className="flex items-center gap-2 text-left flex-1 group"
          data-testid="caption-toggle-btn"
          aria-expanded={!collapsed}
        >
          <div className="flex-1 min-w-0">
            <span className="overline">Testo del post</span>
            <h4 className="font-editorial text-[16px] text-[#1C1C1A] leading-tight mt-0.5">
              {occasion === "open_house"
                ? "Invito Open House."
                : occasion === "venduto"
                ? "Post celebrativo: venduto."
                : "Il testo del tuo post."}
            </h4>
            <p className="text-[11px] text-[#9A968B] leading-snug mt-0.5">
              {occasion === "open_house"
                ? "Il testo è scritto come invito a porte aperte, con data e ora dell'evento."
                : occasion === "venduto"
                ? "Il testo celebra il rogito firmato — sobrio, niente prezzo, niente trionfalismi."
                : "Scegli il tono qui sotto, poi te lo scriviamo a partire dai dati del tuo immobile."}
            </p>
          </div>
          {collapsed ? (
            <CaretDown size={18} weight="bold" className="text-[#6B6A65] group-hover:text-[#1C1C1A] transition-colors flex-shrink-0" />
          ) : (
            <CaretUp size={18} weight="bold" className="text-[#6B6A65] group-hover:text-[#1C1C1A] transition-colors flex-shrink-0" />
          )}
        </button>
        {captions && !collapsed && (
          <button
            onClick={onGenerate}
            disabled={loading}
            className="ml-3 text-[11px] text-[#6B6A65] hover:text-[#1C1C1A] underline underline-offset-2 transition-colors disabled:opacity-50"
            data-testid="caption-regenerate-btn"
          >
            Rigenera
          </button>
        )}
      </div>

      {!collapsed && (<>
      {/* iter v28 r38 — Selettore tono (4 chip): single source of truth.
          Selezionando un tono qui si invalidano i risultati e si aggiorna
          anche il "Tono del testo nello zip" nel PackBox. */}
      {setTone && (
        <div className="mb-3" data-testid="caption-tone-selector">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#9A968B] mb-1.5">
            Tono del testo
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {[
              { v: "moderno",   lbl: "Moderno",   hint: "Pulito, dinamico" },
              { v: "elegante",  lbl: "Elegante",  hint: "Editoriale, curato" },
              { v: "minimal",   lbl: "Minimal",   hint: "Asciutto, secco" },
              { v: "lussuoso",  lbl: "Lussuoso",  hint: "Cinematico, sobrio" },
            ].map((opt) => {
              const active = (tone || "elegante") === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setTone(opt.v)}
                  className={`px-2.5 py-1.5 rounded-md text-left transition-colors border ${
                    active
                      ? "bg-[#7C8B6E] text-white border-[#7C8B6E]"
                      : "bg-white text-[#3F3D38] border-[#E5E7E0] hover:border-[#7C8B6E]"
                  }`}
                  data-testid={`tone-chip-${opt.v}`}
                  aria-pressed={active}
                >
                  <div className="text-[11.5px] font-semibold leading-tight">{opt.lbl}</div>
                  <div className={`text-[9.5px] mt-0.5 ${active ? "text-white/80" : "text-[#9A968B]"}`}>
                    {opt.hint}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!captions && !loading && (
        <>
          <p className="text-[12px] text-[#6B6A65] leading-snug mb-3">
            Moderno · Elegante · Minimal · Lussuoso. Te li scriviamo a partire dai dati del tuo
            immobile, tu copi quello che suona meglio.
          </p>
          <button
            onClick={onGenerate}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#7C8B6E] hover:bg-[#90A082] text-white text-[13px] font-semibold transition-colors"
            data-testid="caption-generate-btn"
          >
            <Sparkle size={15} weight="fill" /> Scrivi 4 versioni
          </button>
        </>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-8 gap-3" data-testid="caption-loading">
          <BrandSpinner />
          <p className="text-[11.5px] text-[#9A968B] italic text-center leading-relaxed">
            Stiamo scrivendo 4 versioni in parallelo.<br />
            Ci mettiamo 5–12 secondi.
          </p>
        </div>
      )}

      {captions && captions.length > 0 && (
        <div className="space-y-2.5" data-testid="caption-results">
          {captions.map((c, i) => {
            const failed = c.text?.startsWith("[Generazione fallita");
            return (
              <div
                key={c.tone}
                className="border border-[#EAE7DF] rounded-md p-3 bg-[#FAFAF7]"
                data-testid={`caption-card-${c.tone}`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#7C8B6E]">
                    {c.label}
                  </span>
                  {!failed && (
                    <button
                      onClick={() => onCopy(c.text, i)}
                      className="inline-flex items-center gap-1 text-[11px] text-[#6B6A65] hover:text-[#1C1C1A] transition-colors"
                      data-testid={`caption-copy-${c.tone}`}
                    >
                      {copiedIdx === i ? (
                        <>
                          <Check size={12} weight="bold" /> Copiato
                        </>
                      ) : (
                        <>
                          <Copy size={12} weight="bold" /> Copia
                        </>
                      )}
                    </button>
                  )}
                </div>
                {failed ? (
                  <p className="text-[11.5px] text-[#9A968B] italic">
                    Tono non generato. Tocca <strong>Rigenera</strong> per riprovare.
                  </p>
                ) : (
                  <p className="text-[12.5px] text-[#1C1C1A] leading-relaxed whitespace-pre-line">
                    {c.text}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>)}
    </div>
  );
}
